import { useQuery } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { ClassRoomsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, createOpenApiConfiguration } from "@/lib/utils";

/**
 * Public class-room detail - optional auth, same pattern as `useLocationGetPublic`.
 */
export const useClassRoomGetPublic = (
  classRoomId: number,
  options?: { enabled?: boolean },
) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  const { data, isError, isLoading, isSuccess, isPending, isFetching, refetch, error } = useQuery({
    queryKey: [QueryKeys.ClassRooms, tenantId, classRoomId, "public"],
    queryFn: () => {
      if (accessToken) {
        const service = createAuthenticatedApi(ClassRoomsApi, accessToken);
        return service.getClassRoomById({ id: classRoomId, xTenantID: tenantId });
      }
      const service = new ClassRoomsApi(createOpenApiConfiguration());
      return service.getClassRoomById({ id: classRoomId, xTenantID: tenantId });
    },
    enabled: options?.enabled !== false && classRoomId > 0,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
    error,
  };
};
